import Typography from "@material-ui/core/Typography";
import React, { FC, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { GridToolbarContainer } from "@material-ui/data-grid";

import BaseDataTable from "../../../../../components/modules/BaseDataTable";
import Loader from "../../../../../components/elements/Loader";
import api from "../../../../../api";
import classes from "../../CampaignsDashboard.module.scss";
import { BrandgenCampaignPool, TargetingSelections } from "../../../../../types/campaignTypes";
import { ColumnType } from "../../../../../components/modules/BaseDataTable/BaseDataTable";

type CampaignReportDashboardProps = {
  campaign: BrandgenCampaignPool;
};

const fetchTargetingSelections = async (brandgenCampaignId: string) => {
  try {
    const { payload } = await api.getTargetingSelections(brandgenCampaignId);
    return payload;
  } catch (error) {
    const err = error as AxiosError;
    throw new Error(err.message);
  }
};

const CampaignTargetingSelectionsDashboard: FC<CampaignReportDashboardProps> = ({ campaign }) => {
  const columns: ColumnType[] = [
    { key: "domain_list", label: "Domain lists", minWidth: 100 },
    { key: "geo_state", label: "Geo states", minWidth: 120 },
    { key: "geo_zip", label: "Zip codes", minWidth: 120, customStyles: { columnCount: 3, columnGap: "20px" } },
    { key: "role_title_segments", label: "Role/Title", minWidth: 70 },
    { key: "retargeting_segments", label: "Retargeting segments", minWidth: 100 },
  ];

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [targetingSelections, setTargetingSelections] = useState<TargetingSelections>();

  useEffect(() => {
    const fetchAndSetTargetingSelections = async () => {
      try {
        setIsLoading(true);
        const fetchedTargetingSelections = await fetchTargetingSelections(campaign.brandgenCampaignPoolId);
        setTargetingSelections(fetchedTargetingSelections);
        setIsLoading(false);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log("fetchAndSetTargetingSelections error: ", error);
        setIsLoading(false);
      }
    };

    fetchAndSetTargetingSelections();
  }, [campaign.brandgenCampaignPoolId]);

  const joinValues = (values: Array<string> | undefined | null) => {
    if (!values || values.length === 0) return " N/A ";
    let result = "";

    values.forEach((value) => {
      if (result.length === 0) result = `${result + value}\n`;
      else result += `${value}\n`;
    });
    return result;
  };

  const campaignTargetingSelections = {
    id: campaign.brandgenCampaignPoolId,
    domain_list: joinValues(
      targetingSelections?.domain_list?.map((d: { name?: string } | string) =>
        typeof d === "string" ? d : (d as { name?: string })?.name ?? "",
      ),
    ),
    geo_state: joinValues(targetingSelections?.geo_state?.map((s: { name: string }) => s.name)),
    geo_zip: joinValues(targetingSelections?.geo_zip),
    role_title_segments: joinValues(
      targetingSelections?.role_title_segments?.map((r: { role_name: string }) => r.role_name),
    ),
    retargeting_segments: joinValues(targetingSelections?.retargeting_segments),
  };

  const rows = [
    {
      ...campaignTargetingSelections,
    },
  ];

  return (
    <div className={classes.targetingSelectionsDashboard}>
      <GridToolbarContainer className={classes.toolbar}>
        <Typography variant="h3">Campaign Targeting Selections</Typography>
      </GridToolbarContainer>
      {isLoading ? (
        <Loader className={classes.loader} isAbsolutePosition={false} size={42} />
      ) : (
        <BaseDataTable columns={columns} rows={rows} />
      )}
    </div>
  );
};

export default CampaignTargetingSelectionsDashboard;
